/* eslint-disable prefer-const */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAutomationRuleActions } from '../use-actions';
import { ActionItemType, EditWorkItemActionType, EditWorkItemErrorsType } from '../types';
import { useEditWorkItem } from './use-edit-work-item';
import { useAzureProvider } from 'src/azure/context';
import Suspense from 'src/components/shared/suspense-component';
import WorkItemFieldInput from './work-item-field';
import { useWorkItemFieldHook } from './work-item-field/use-work-item-field';
import FieldActionInput from './field-action';
import TextInput from './text-input';
import UserFieldInput from './user-field';
import IterationAndAreaPathInput from './iteration-and-area-path';
import SelectInput from './select-input';
import DateTimeFormInput from '../../date-time-input';
import { useDatetimeInputWithActions } from '../../date-time-input/use-with-actions';
import { editWorkItemStyles } from './styles';
import { Skeleton, SkeletonItem } from '@fluentui/react-components';
import CopyFieldValueInput, { useCopyFieldValueInputRef } from './copy-field-value';

interface Props {
	actionId: string;
	validations: EditWorkItemErrorsType | null;
}

export default function EditWorkItem(props: Props) {
	const classes = editWorkItemStyles();
	const {applicationContext} = useAzureProvider();

	const copyFieldValueInputRef = useCopyFieldValueInputRef();

	const [onInitialLoading, setOnInitialLoading] = useState(false);
	const useWorkItemFieldInput = useWorkItemFieldHook();
	const editWorkItemHook = useEditWorkItem();

	const dateTimeInput = useDatetimeInputWithActions();

	const {actions, setActions} = useAutomationRuleActions();

	const [inputs, setInputs] = useState<{
		workItemField: {
			initialOptions: Array<{label: string; value: string}>,
			loading: boolean,
		},
		value: boolean,
	}>({
		workItemField: {
			initialOptions: [],
			loading: false,
		},
		value: false,
	});

	const current = useMemo(() => {
		const found = actions.find(item => item.id === props.actionId) as ActionItemType;
		return found.editWorkItem as EditWorkItemActionType;
	}, [actions, props.actionId]);

	const isCopyFieldValue = useMemo(() => {
		return current.fieldActionValue === 'copy-field-value' || current.fieldActionValue === 'copy-field-value-from-parent-work-item';
	}, [current.fieldActionValue]);

	const isUserFieldType = useMemo(() => {
		return current.inputValueType === 'user-field' && !isCopyFieldValue;
	}, [current.inputValueType, isCopyFieldValue]);

	const isSelectFieldType = useMemo(() => {
		return current.inputValueType === 'select' && !isCopyFieldValue;
	}, [current.inputValueType, isCopyFieldValue]);

	const isMultiSelect = useMemo(() => {
		return current.inputValueType === 'multi-select' && !isCopyFieldValue;
	}, [current.inputValueType, isCopyFieldValue]);

	const isClearField = useMemo(() => {
		return current.fieldActionValue === 'clear-field' && !isCopyFieldValue;
	}, [current.fieldActionValue, isCopyFieldValue]);

	const isTextOrNumberInput = useMemo(() => {
		return (current.inputValueType === 'number' || current.inputValueType === 'text') && !isCopyFieldValue;
	}, [current.inputValueType, isCopyFieldValue]);

	const isDateTimeInput = useMemo(() => {
		return current.inputValueType === 'datetime' && !isCopyFieldValue;
	}, [current.inputValueType, isCopyFieldValue]);

	const isAreaPathOrIterationPath = useMemo(() => {
		return current.inputValueType === 'iteration-or-area-path' && !isCopyFieldValue;
	}, [current.inputValueType, isCopyFieldValue]);

	const changeWorkItemField = useCallback((fieldName: string) => {
		setInputs(oldValue => ({ ...oldValue, value: true }));

		editWorkItemHook.getFieldValueType(fieldName)
			.then((result) => {
				setActions((oldActions) => {
					const current = oldActions.find(item => item.id === props.actionId) as ActionItemType;
					const others = oldActions.filter(item => item.id !== props.actionId);
		
					return [...others, {
						...current, editWorkItem: {
							...current.editWorkItem as EditWorkItemActionType,
							inputValueType: result
						}
					}];
				});
			})
			.finally(() => {
				setInputs((oldValues) => ({ ...oldValues, value: false }));
				if (copyFieldValueInputRef.current) {
					copyFieldValueInputRef.current.updateOptions({
						workItemField: fieldName,
						projectId: applicationContext.projectId,
					});
				}
			});
	}, [
		editWorkItemHook,
		props.actionId,
		applicationContext.projectId,
		copyFieldValueInputRef,
		setActions,
		setInputs,
	]);

	useEffect(() => {
		const asyncHandler = async () => {
			setOnInitialLoading(true);
			const setWorkItemStateValue = (values: Partial<typeof inputs['workItemField']>) => {
				setInputs((oldValues) => ({
					...oldValues,
					workItemField: {
						...oldValues.workItemField,
						...values
					}
				}));
			};

			setWorkItemStateValue({ loading: true });
			const results = await useWorkItemFieldInput.getWorkItemFieldOptions({
				projectId: applicationContext.projectId,
			});

			setWorkItemStateValue({ loading: false, initialOptions: results });
			setOnInitialLoading(false);
		};

		asyncHandler();
	}, []);

	if (onInitialLoading) {
		return (
			<Skeleton className={classes.skeletonRoot}>
				<SkeletonItem className={classes.skeletonItem} />
			</Skeleton>
		);
	}

	return (
		<div className={classes.root}>
			<div className={classes.columnFlex}>
				<Suspense isLoading={inputs.workItemField.loading}>
					<WorkItemFieldInput
						actionId={props.actionId}
						validationError={props.validations?.workItemField || null}
						initialOptions={inputs.workItemField.initialOptions}
						onChangeWorkItemField={changeWorkItemField}
					/>
				</Suspense>
			</div>
			<div className={classes.columnFlex}>
				{(current.workItemField && (
					<Suspense
						isLoading={inputs.value}
					>
						<FieldActionInput
							actionId={props.actionId}
							validationError={props.validations?.fieldActionValue || null}
						/>
					</Suspense>
				))}
			</div>
			<div className={classes.columnFlex}>
				{(!isClearField && current.fieldActionValue && isAreaPathOrIterationPath) && (
					<IterationAndAreaPathInput
						actionId={props.actionId}
						validationError={props.validations?.iterationOrAreaPathValue || null}
					/>
				)}
				{(!isClearField && current.fieldActionValue && isUserFieldType) && (
					<UserFieldInput
						actionId={props.actionId}
						validationError={props.validations?.userFieldValue || null}
					/>
				)}
				{(!isClearField && current.fieldActionValue && isSelectFieldType) && (
					<SelectInput
						actionId={props.actionId}
						validationError={props.validations?.selectValue || null}
					/>
				)}
				{(!isClearField && current.fieldActionValue && isMultiSelect) && (
					<SelectInput
						actionId={props.actionId}
						validationError={props.validations?.userFieldValue || null}
						isMultiSelect={true}
					/>
				)}
				{(!isClearField && current.fieldActionValue && isTextOrNumberInput) && (
					<TextInput
						actionId={props.actionId}
						type={current.inputValueType as 'text' | 'number'}
						validationError={props.validations?.textValue || null}
					/>
				)}
				{(!isClearField && current.fieldActionValue && isDateTimeInput) && (
					<DateTimeFormInput
						type={current.dateTimeValue.type}
						dateTime={{
							onChange: dateTimeInput.onChangeDateTime(props.actionId),
							value: current.dateTimeValue.datetime.value,
						}}
						timeframe={{
							onChange: dateTimeInput.onChangeTimeframe(props.actionId),
							value: {
								timeNumber: current.dateTimeValue.timeframe.timeNumber,
								timeUnit: current.dateTimeValue.timeframe.timeUnit,
								timeOption: current.dateTimeValue.timeframe.timeOption,
							},
						}}
					/>
				)}
				{!isClearField && isCopyFieldValue && (
					<CopyFieldValueInput
						ref={copyFieldValueInputRef}
						actionId={props.actionId}
						validationError={props.validations?.copyFieldValue || null}
					/>
				)}
			</div>
		</div>
	);
}
